// Educational Content for K-Beauty Loading Screens
// Displays rotating slides during API analysis (10-30 seconds)

export interface EducationSlide {
  id: string
  title: string
  content: string
  tip?: string
  icon: string
  duration: number // milliseconds
}

// ============================================================================
// K-Beauty Education Slides
// ============================================================================

export const EDUCATION_SLIDES: EducationSlide[] = [
  {
    id: 'layering',
    title: 'The K-Beauty Layering Effect',
    content:
      'Korean skincare applies products from thinnest to thickest consistency. This allows each layer to absorb fully before the next, maximizing ingredient penetration.',
    tip: 'Think: water → essence → serum → cream',
    icon: '💧',
    duration: 6000,
  },
  {
    id: 'am-vs-pm',
    title: 'AM vs PM: Different Needs',
    content:
      'Morning routines focus on protection (antioxidants, sunscreen). Evening routines focus on repair and treatment (retinol, acids, sleeping masks).',
    tip: 'Never skip sunscreen in the morning!',
    icon: '🌅',
    duration: 6000,
  },
  {
    id: 'double-cleanse',
    title: 'Double Cleansing: The K-Beauty Secret',
    content:
      'Oil-based cleanser removes makeup and sunscreen. Water-based cleanser removes sweat and impurities. Together, they achieve deeper cleansing without stripping.',
    tip: 'Only double cleanse at night',
    icon: '🧴',
    duration: 6000,
  },
  {
    id: 'toner-essence-serum',
    title: "Toner → Essence → Serum: What's the Difference?",
    content:
      'Toner preps skin and balances pH. Essence hydrates and boosts absorption. Serum delivers concentrated active ingredients to target specific concerns.',
    tip: 'You can skip essence if using a hydrating toner',
    icon: '✨',
    duration: 6000,
  },
  {
    id: '7-skin-method',
    title: 'The 7-Skin Method',
    content:
      'A K-beauty technique of applying 7 thin layers of toner or essence for intense hydration. Perfect for dehydrated skin that needs a moisture boost.',
    tip: 'Start with 3 layers and build up',
    icon: '💦',
    duration: 5000,
  },
  {
    id: 'glass-skin',
    title: 'Glass Skin: The Ultimate Goal',
    content:
      'The famous "glass skin" look comes from deep hydration and a healthy skin barrier. It takes consistent care—not just products—to achieve this luminous, clear complexion.',
    tip: 'Hydration is the foundation of glass skin',
    icon: '🪞',
    duration: 5000,
  },
  {
    id: 'sheet-masks',
    title: 'Sheet Masks: Weekly Ritual',
    content:
      'Korean sheet masks deliver concentrated essences through 15-20 minutes of contact time. Use 1-2 times weekly for a hydration boost or before special events.',
    tip: 'Pat in the remaining essence after removing',
    icon: '🎭',
    duration: 5000,
  },
  {
    id: 'ingredients-k-beauty',
    title: 'Hero Ingredients in K-Beauty',
    content:
      'Snail mucin for repair, centella (cica) for calming, rice for brightening, ginseng for anti-aging, and green tea for antioxidants. These traditional ingredients are backed by science.',
    tip: 'Start with one new ingredient at a time',
    icon: '🌿',
    duration: 6000,
  },
]

// ============================================================================
// Progress-Based Messages
// ============================================================================

export const PROGRESS_MESSAGES: { progress: number; message: string }[] = [
  { progress: 10, message: 'Analyzing your skin texture...' },
  { progress: 25, message: 'Detecting moisture levels...' },
  { progress: 40, message: 'Evaluating pore visibility...' },
  { progress: 55, message: 'Checking for signs of aging...' },
  { progress: 70, message: 'Analyzing skin tone and brightness...' },
  { progress: 85, message: 'Checking your local weather...' },
  { progress: 95, message: 'Building your personalized routine...' },
]

// ============================================================================
// Weather Loading Messages
// ============================================================================

export const WEATHER_MESSAGES = [
  'Checking the weather in your area...',
  'Analyzing local humidity levels...',
  'Factoring in UV exposure...',
  'Customizing for your climate...',
]

// ============================================================================
// Routine Phases for UI
// ============================================================================

export const ROUTINE_PHASES = [
  {
    id: 'cleanse',
    name: 'Cleanse',
    koreanName: '클렌징',
    description: 'Remove impurities and prep skin',
    color: '#60A5FA',
    icon: '🧴',
  },
  {
    id: 'prep',
    name: 'Prep',
    koreanName: '프렙',
    description: 'Balance and hydrate',
    color: '#34D399',
    icon: '💧',
  },
  {
    id: 'nourish',
    name: 'Nourish',
    koreanName: '영양공급',
    description: 'Target concerns with actives',
    color: '#A78BFA',
    icon: '✨',
  },
  {
    id: 'protect',
    name: 'Protect',
    koreanName: '보호',
    description: 'Shield from UV and environment',
    color: '#FBBF24',
    icon: '☀️',
  },
]

// ============================================================================
// Skin Concern Explanations for Report
// ============================================================================

export const CONCERN_EXPLANATIONS: Record<
  string,
  {
    title: string
    whatItMeans: string
    ingredients: string[]
    lifestyle: string
  }
> = {
  wrinkle: {
    title: 'Fine Lines & Wrinkles',
    whatItMeans:
      'Signs of reduced collagen and elastin, often caused by sun exposure, dehydration, or natural aging.',
    ingredients: ['Retinol', 'Peptides', 'Adenosine', 'PDRN'],
    lifestyle: 'Use sunscreen daily, stay hydrated, and consider a humidifier at night.',
  },
  pore: {
    title: 'Enlarged Pores',
    whatItMeans: 'Pores appear larger due to excess sebum, loss of elasticity, or clogged follicles.',
    ingredients: ['Niacinamide', 'BHA (Salicylic Acid)', 'Retinol', 'Clay'],
    lifestyle: 'Cleanse thoroughly, avoid heavy makeup, and never skip double cleansing.',
  },
  texture: {
    title: 'Uneven Texture',
    whatItMeans: 'Rough or bumpy skin often caused by dead cell buildup, dehydration, or congestion.',
    ingredients: ['AHA (Glycolic Acid)', 'PHA', 'Snail Mucin', 'Centella'],
    lifestyle: 'Exfoliate 2-3x weekly and maintain consistent hydration.',
  },
  acne: {
    title: 'Acne & Breakouts',
    whatItMeans: 'Caused by excess oil, bacteria, clogged pores, or hormonal factors.',
    ingredients: ['Salicylic Acid (BHA)', 'Tea Tree', 'Centella', 'Propolis'],
    lifestyle: 'Keep hands off face, change pillowcases often, and avoid pore-clogging products.',
  },
  oiliness: {
    title: 'Excess Oiliness',
    whatItMeans: 'Overactive sebaceous glands, often triggered by dehydration or hormones.',
    ingredients: ['Niacinamide', 'BHA', 'Green Tea', 'Centella'],
    lifestyle: 'Hydrate well—dehydrated skin often overproduces oil to compensate.',
  },
  radiance: {
    title: 'Dull Skin & Tone',
    whatItMeans: 'Lack of brightness often from dead cell buildup, poor circulation, or dehydration.',
    ingredients: ['Vitamin C', 'Niacinamide', 'AHA', 'Rice Extract'],
    lifestyle: 'Exfoliate regularly, stay hydrated, and consider facial massage.',
  },
  moisture: {
    title: 'Dehydration',
    whatItMeans: 'Lack of water in the skin causing tightness, flakiness, or fine lines.',
    ingredients: ['Hyaluronic Acid', 'Ceramides', 'Beta-Glucan', 'Panthenol'],
    lifestyle: 'Drink water, use a humidifier, and layer hydrating products.',
  },
  redness: {
    title: 'Redness & Sensitivity',
    whatItMeans: 'Inflammation or reactive skin, possibly from irritation or a weakened barrier.',
    ingredients: ['Centella (Cica)', 'Madecassoside', 'Aloe Vera', 'Panthenol'],
    lifestyle: 'Avoid harsh products, fragrance, and alcohol. Strengthen skin barrier.',
  },
  firmness: {
    title: 'Loss of Firmness',
    whatItMeans: 'Reduced collagen and elastin causing sagging or less bouncy skin.',
    ingredients: ['Peptides', 'PDRN', 'EGF', 'Adenosine'],
    lifestyle: 'Protect from sun, consider facial exercises, maintain consistent routine.',
  },
  age_spot: {
    title: 'Hyperpigmentation',
    whatItMeans: 'Dark spots from sun damage, hormones, or post-inflammatory marks.',
    ingredients: ['Vitamin C', 'Niacinamide', 'Arbutin', 'Tranexamic Acid'],
    lifestyle: 'Sunscreen is critical! Sun exposure darkens existing spots.',
  },
  dark_circle: {
    title: 'Dark Circles',
    whatItMeans: 'Caused by genetics, thin skin, lack of sleep, or blood vessel visibility.',
    ingredients: ['Vitamin C', 'Peptides', 'Caffeine', 'Niacinamide'],
    lifestyle: 'Get adequate sleep, reduce screen time before bed, stay hydrated.',
  },
  eye_bag: {
    title: 'Under-Eye Puffiness',
    whatItMeans: 'Fluid retention, allergies, or lack of sleep causing swelling.',
    ingredients: ['Caffeine', 'Green Tea', 'Peptides', 'Cooling ingredients'],
    lifestyle: 'Sleep elevated, reduce salt intake, apply cold compress in morning.',
  },
}
